/**
 * Single shared configuration draft for the pool detail page.
 *
 * All config-editing tabs (Scaling, Predictive, Infrastructure) read and
 * patch the same draft, and ONE serializer produces the settings JSON —
 * replacing the two divergent serializers that used to live in the list
 * page's edit modal and the detail page's EditPoolForm. The serializer
 * below is a direct port of EditPoolForm.handleSubmit, which was the
 * authoritative one.
 *
 * Hydration: the draft is (re)built from the pool row whenever the pool
 * loads or is refetched after a save, but never while the operator has
 * unsaved edits (`dirty`) — the 10s runtime auto-refresh must not clobber
 * an in-progress edit.
 */

import { useCallback, useEffect, useState } from 'react';
import type { CapacitySchedule } from '@interlucent/organization-protocol';
import type { InfrastructureFormData } from '../../types/profile.types';
import type { AdminPool } from './shared';

export type DemandSourceType = 'reactive' | 'scheduled' | 'predictive' | 'backfill';
export type RadiusTier = 'edge' | 'regional' | 'continental' | 'global';

export interface PoolConfigDraft {
  name: string;
  region: string;
  active: boolean;
  tenancy: 'single' | 'organization' | 'multi';
  scalingMin: string;
  scalingMax: string;
  /**
   * Legacy floor-padding knob, preserved but not exposed in the UI. Backfill
   * provides headroom via a ratio now; new pools default to 0.
   */
  scalingBuffer: string;
  orchestratorVersion: string;
  enabledSources: Set<DemandSourceType>;
  /** Shadow runs the predictive pipeline but contributes floor=0 to mediation. */
  predictiveShadowMode: boolean;
  scaleUpThreshold: string;
  scaleDownDelay: string;
  cooldownSeconds: string;
  evaluationPeriods: string;
  preScaleMinutes: string;
  lookaheadMinutes: string;
  predictiveQuantile: string;
  predictiveTimezone: string;
  backfillThreshold: string;
  backfillMultiplier: string;
  backfillAdaptive: boolean;
  backfillAdaptiveWindow: string;
  backfillAdaptiveSaturation: string;
  /**
   * Soft floor — opt-in dip below the min clamp / scheduled floors after
   * extended full idleness. Predictive floors are never dipped; release is
   * instant on any demand.
   */
  softFloorEnabled: boolean;
  softFloorAfterIdleSeconds: string;
  softFloorMinSlots: string;
  /**
   * Drain reaper — per-instance scale-down actuation (default ON). Grace and
   * immunity default to 720s = 1.5× the fleet's ~8 min cold start; pools with
   * materially different boot times should override both to ~1.5× theirs.
   */
  reaperEnabled: boolean;
  reaperIdleGraceSeconds: string;
  reaperBootImmunitySeconds: string;
  reaperMaxReapsPerTick: string;
  observationRadiusTier: RadiusTier;
  infrastructure: InfrastructureFormData;
  capacitySchedule: CapacitySchedule | undefined;
}

export interface SerializedPoolConfig {
  settingsJson: string;
  capacityScheduleJson: string | null;
  name: string;
  region: string;
  active: boolean;
}

const DEFAULT_INFRA: InfrastructureFormData = {
  backends: [],
  allocationStrategy: 'balanced',
};

// Migrate the legacy tier names from older configs into the current set.
function migrateRadiusTier(raw: string | undefined): RadiusTier {
  switch (raw) {
    case 'local':
      return 'edge';
    case 'intercontinental':
      return 'continental';
    case 'edge':
    case 'regional':
    case 'continental':
    case 'global':
      return raw;
    default:
      return 'global';
  }
}

function parseSettings(pool: AdminPool): Record<string, any> | null {
  if (!pool.settingsJson) return null;
  try { return JSON.parse(pool.settingsJson); } catch { return null; }
}

function hydrate(pool: AdminPool): PoolConfigDraft {
  const existingSettings = parseSettings(pool);

  const infrastructure: InfrastructureFormData = existingSettings
    ? (() => {
        const backends = (existingSettings.backends ?? []).map((b: any) => ({
          ...b,
          awsSubnets: Array.isArray(b.awsSubnets) ? b.awsSubnets.join(', ') : b.awsSubnets,
          awsSecurityGroups: Array.isArray(b.awsSecurityGroups) ? b.awsSecurityGroups.join(', ') : b.awsSecurityGroups,
          gcpFirewallTags: Array.isArray(b.gcpFirewallTags) ? b.gcpFirewallTags.join(', ') : b.gcpFirewallTags,
          // CoreWeave: convert server format back to form format
          cwRootPvcLabel: b.cwRootPvcLabel && typeof b.cwRootPvcLabel === 'object'
            ? Object.entries(b.cwRootPvcLabel).map(([k, v]) => `${k}=${v}`).join(', ')
            : b.cwRootPvcLabel,
          cwAdditionalDiskSize: Array.isArray(b.cwAdditionalDisks) && b.cwAdditionalDisks[0]?.size
            ? b.cwAdditionalDisks[0].size
            : b.cwAdditionalDiskSize,
        }));
        return {
          backends: backends.length ? backends : DEFAULT_INFRA.backends,
          allocationStrategy: existingSettings.allocationStrategy ?? 'balanced',
        };
      })()
    : DEFAULT_INFRA;

  // Demand sources — derive from legacy strategy type or explicit config
  const derivedSources = (() => {
    if (existingSettings?.demandSources) return existingSettings.demandSources;
    const t = existingSettings?.strategy?.type;
    if (t === 'scheduled-strict') return { reactive: false, scheduled: true, predictive: false };
    if (t === 'scheduled' || t === 'scheduled-floor') return { reactive: true, scheduled: true, predictive: false };
    if (t === 'predictive') return { reactive: true, scheduled: true, predictive: true };
    return { reactive: true, scheduled: false, predictive: false };
  })();

  const enabledSources = new Set<DemandSourceType>();
  if (derivedSources.reactive !== false) enabledSources.add('reactive');
  if (derivedSources.scheduled) enabledSources.add('scheduled');
  // Either flag enables the predictive section. Shadow vs. consume is a
  // sub-mode toggled within the section.
  if (derivedSources.predictive || (existingSettings?.demandSources as { predictiveShadow?: boolean })?.predictiveShadow) {
    enabledSources.add('predictive');
  }
  if (derivedSources.backfill !== false) enabledSources.add('backfill');

  let capacitySchedule: CapacitySchedule | undefined;
  if (pool.capacityScheduleJson) {
    try { capacitySchedule = JSON.parse(pool.capacityScheduleJson); } catch { /* ignore */ }
  }

  return {
    name: pool.name,
    region: pool.region,
    active: pool.active,
    tenancy: existingSettings?.tenancy ?? (pool.type === 'by_request' ? 'multi' : 'single'),
    scalingMin: String(existingSettings?.scaling?.min ?? 0),
    // 100 session slots ≈ 25 workers at typical maxConcurrent=4. Kept in sync
    // with the backend default in OrgWsDO; the old value of 10 silently
    // clamped pools at ~3 concurrent users once backfill's 3x headroom
    // kicked in. See F3 in the pool defaults review.
    scalingMax: String(existingSettings?.scaling?.max ?? 100),
    scalingBuffer: String(existingSettings?.scaling?.buffer ?? 0),
    orchestratorVersion: existingSettings?.orchestratorVersion ?? '',
    enabledSources,
    predictiveShadowMode:
      (existingSettings?.demandSources as { predictiveShadow?: boolean })?.predictiveShadow === true,
    scaleUpThreshold: String(existingSettings?.strategy?.scaleUpThreshold ?? 1),
    scaleDownDelay: String(existingSettings?.strategy?.scaleDownDelaySeconds ?? 900),
    cooldownSeconds: String(existingSettings?.strategy?.cooldownSeconds ?? 120),
    evaluationPeriods: String(existingSettings?.strategy?.evaluationPeriods ?? 1),
    preScaleMinutes: String(existingSettings?.strategy?.preScaleMinutes ?? 15),
    lookaheadMinutes: String(existingSettings?.strategy?.lookaheadMinutes ?? 30),
    // Predictive histogram knobs (IL-136). Default quantile 0.9 per the
    // June-2026 closed-loop replay (seeded P90/30m). Timezone is IANA; empty
    // string means "inherit UTC".
    predictiveQuantile: String(existingSettings?.predictiveQuantile ?? 0.9),
    predictiveTimezone: String(existingSettings?.timezone ?? ''),
    backfillThreshold: String(existingSettings?.backfill?.threshold ?? 0.5),
    backfillMultiplier: String(existingSettings?.backfill?.growthMultiplier ?? 1.5),
    backfillAdaptive: existingSettings?.backfill?.adaptive === true,
    backfillAdaptiveWindow: String(existingSettings?.backfill?.adaptiveWindowMinutes ?? 5),
    backfillAdaptiveSaturation: String(existingSettings?.backfill?.adaptiveSaturationArrivalsPerMin ?? 1),
    softFloorEnabled: existingSettings?.softFloor?.enabled === true,
    softFloorAfterIdleSeconds: String(existingSettings?.softFloor?.afterIdleSeconds ?? 3600),
    softFloorMinSlots: String(existingSettings?.softFloor?.minSlots ?? 0),
    reaperEnabled: existingSettings?.reaper?.enabled !== false,
    reaperIdleGraceSeconds: String(existingSettings?.reaper?.idleGraceSeconds ?? 720),
    reaperBootImmunitySeconds: String(existingSettings?.reaper?.bootImmunitySeconds ?? 720),
    reaperMaxReapsPerTick: String(existingSettings?.reaper?.maxReapsPerTick ?? 5),
    observationRadiusTier: migrateRadiusTier(existingSettings?.observation?.radiusTier as string | undefined),
    infrastructure,
    capacitySchedule,
  };
}

export interface PoolConfigDraftApi {
  /** Null until the pool row has loaded. */
  draft: PoolConfigDraft | null;
  patch: (updates: Partial<PoolConfigDraft>) => void;
  addSource: (source: DemandSourceType) => void;
  removeSource: (source: DemandSourceType) => void;
  restoreDemandSourceDefaults: () => void;
  dirty: boolean;
  /** Throw away unsaved edits and rebuild the draft from the pool row. */
  discard: () => void;
  /** Clear the dirty flag after a successful save. */
  markSaved: () => void;
  serialize: () => SerializedPoolConfig | null;
  /**
   * Explicit radiusKm power-user override present in raw settings. When set,
   * the tier selector is disabled and this value is preserved on save.
   */
  existingRadiusKmOverride: number | null;
}

export function usePoolConfigDraft(pool: AdminPool | null): PoolConfigDraftApi {
  const [draft, setDraft] = useState<PoolConfigDraft | null>(null);
  const [dirty, setDirty] = useState(false);

  // Hydrate when the pool row (re)loads, but never over unsaved edits.
  useEffect(() => {
    if (!pool) return;
    setDraft(prev => {
      if (prev && dirty) return prev;
      return hydrate(pool);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool?.settingsJson, pool?.capacityScheduleJson, pool?.name, pool?.region, pool?.active, pool?.id]);

  const patch = useCallback((updates: Partial<PoolConfigDraft>) => {
    setDraft(prev => (prev ? { ...prev, ...updates } : prev));
    setDirty(true);
  }, []);

  const addSource = useCallback((source: DemandSourceType) => {
    setDraft(prev => prev
      ? { ...prev, enabledSources: new Set([...prev.enabledSources, source]) }
      : prev);
    setDirty(true);
  }, []);

  const removeSource = useCallback((source: DemandSourceType) => {
    setDraft(prev => {
      if (!prev) return prev;
      const next = new Set(prev.enabledSources);
      next.delete(source);
      return { ...prev, enabledSources: next };
    });
    setDirty(true);
  }, []);

  // Restore demand source configuration to current platform defaults. Resets
  // enabled sources (reactive + backfill on, predictive off, scheduled
  // preserved since it depends on whether the user actually has schedule
  // entries) and clears all tuning knobs back to DEFAULT_STRATEGY_CONFIG
  // values. Does not save — the operator still needs to Save to persist.
  const restoreDemandSourceDefaults = useCallback(() => {
    setDraft(prev => {
      if (!prev) return prev;
      const next = new Set<DemandSourceType>(['reactive', 'backfill']);
      if (prev.enabledSources.has('scheduled')) next.add('scheduled');
      return {
        ...prev,
        enabledSources: next,
        scaleUpThreshold: '1',
        scaleDownDelay: '900',
        cooldownSeconds: '120',
        evaluationPeriods: '1',
        preScaleMinutes: '15',
        lookaheadMinutes: '4',
        predictiveQuantile: '0.75',
        predictiveTimezone: '',
        predictiveShadowMode: false,
        backfillThreshold: '0.5',
        backfillMultiplier: '1.5',
        backfillAdaptive: false,
        backfillAdaptiveWindow: '5',
        backfillAdaptiveSaturation: '1',
        // Reset observation radius to the platform default tier. We do not
        // clear any explicit radiusKm override here — that lives in the raw
        // settings JSON and is only touched if the operator cleared it
        // themselves; restoring "defaults" shouldn't wipe a deliberate override.
        observationRadiusTier: 'global',
      };
    });
    setDirty(true);
  }, []);

  const discard = useCallback(() => {
    if (pool) setDraft(hydrate(pool));
    setDirty(false);
  }, [pool]);

  const markSaved = useCallback(() => setDirty(false), []);

  const existingSettings = pool ? parseSettings(pool) : null;
  const existingRadiusKmOverride = typeof existingSettings?.observation?.radiusKm === 'number'
    ? existingSettings.observation.radiusKm
    : null;

  const serialize = useCallback((): SerializedPoolConfig | null => {
    if (!draft || !pool) return null;
    if (!draft.name.trim() || !draft.region) return null;

    const settings = parseSettings(pool);
    const parseIntVal = (v: string) => { const n = parseInt(v, 10); return isNaN(n) ? undefined : n; };
    const parseNum = (v: string) => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };

    const backends = draft.infrastructure.backends.map(b => {
      const backend: Record<string, unknown> = {
        id: b.id,
        cloud: b.cloud,
        region: b.region,
        instanceType: b.instanceType,
        imageId: b.imageId,
        weight: b.weight,
      };
      if (b.maxCapacity != null) backend.maxCapacity = b.maxCapacity;
      if (b.maxConcurrent != null && b.maxConcurrent > 1) backend.maxConcurrent = b.maxConcurrent;
      if (b.simpleSlots === false) backend.simpleSlots = false;
      if (b.startupMode && b.startupMode !== 'standard') backend.startupMode = b.startupMode;
      if (b.launchProtocolVersion != null && b.launchProtocolVersion !== 1) backend.launchProtocolVersion = b.launchProtocolVersion;
      // Free-form orc CLI args — delivered verbatim under launch protocol v4,
      // mapped onto the typed IL_* knobs under v1/v3.
      const orchestratorArgs = b.orchestratorArgs?.trim();
      if (orchestratorArgs) backend.orchestratorArgs = orchestratorArgs;
      // v3 orchestrator-behavior knobs. Only emit when truthy/set; the read
      // path uses `...b` so absent fields render as undefined which the UI's
      // `?? false` handles.
      if (b.shareWorkspace) backend.shareWorkspace = true;
      if (b.ultraFastSlots) backend.ultraFastSlots = true;
      if (b.profilePool) backend.profilePool = true;
      if (b.acceptConfirm) backend.acceptConfirm = true;
      if (b.noWarm) backend.noWarm = true;
      if (b.instantOn) backend.instantOn = true;
      if (b.precache) backend.precache = true;
      if (b.noJobBoard) backend.noJobBoard = true;
      if (b.uiIsolation) backend.uiIsolation = true;
      if (b.streamingTarzst) backend.streamingTarzst = true;
      if (b.logJsonPath) backend.logJsonPath = b.logJsonPath;
      if (b.logLevel) backend.logLevel = b.logLevel;
      if (b.otelEndpoint) backend.otelEndpoint = b.otelEndpoint;
      if (b.otelAuth) backend.otelAuth = b.otelAuth;
      // AWS
      if (b.awsSubnets) backend.awsSubnets = b.awsSubnets.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsSecurityGroups) backend.awsSecurityGroups = b.awsSecurityGroups.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsInstanceProfile) backend.awsInstanceProfile = b.awsInstanceProfile;
      // GCP
      if (b.gcpProjectId) backend.gcpProjectId = b.gcpProjectId;
      if (b.gcpSubnetwork) backend.gcpSubnetwork = b.gcpSubnetwork;
      if (b.gcpFirewallTags) backend.gcpFirewallTags = b.gcpFirewallTags.split(',').map(s => s.trim()).filter(Boolean);
      if (b.gcpServiceAccount) backend.gcpServiceAccount = b.gcpServiceAccount;
      if (b.gcpGpuVariant) backend.gcpGpuVariant = b.gcpGpuVariant;
      if (b.gcpInstanceModel) backend.gcpInstanceModel = b.gcpInstanceModel;
      // CoreWeave
      if (b.cwApiServer) backend.cwApiServer = b.cwApiServer;
      if (b.cwKubeToken) backend.cwKubeToken = b.cwKubeToken;
      if (b.cwNamespace) backend.cwNamespace = b.cwNamespace;
      if (b.cwGpuCount != null) backend.cwGpuCount = b.cwGpuCount;
      if (b.cwCpuCount != null) backend.cwCpuCount = b.cwCpuCount;
      if (b.cwMemoryGb != null) backend.cwMemoryGb = b.cwMemoryGb;
      if (b.cwRootDiskSize) backend.cwRootDiskSize = b.cwRootDiskSize;
      if (b.cwRootPvcLabel) {
        const labels: Record<string, string> = {};
        b.cwRootPvcLabel.split(',').forEach(pair => {
          const [k, v] = pair.split('=').map(s => s.trim());
          if (k && v) labels[k] = v;
        });
        if (Object.keys(labels).length > 0) backend.cwRootPvcLabel = labels;
      }
      if (b.cwAdditionalDiskSize) {
        backend.cwAdditionalDisks = [{ name: 'data', size: b.cwAdditionalDiskSize }];
      }
      if (b.cwVsUsername) backend.cwVsUsername = b.cwVsUsername;
      if (b.cwVsPassword) backend.cwVsPassword = b.cwVsPassword;
      return backend;
    });

    // Build strategy config (tuning knobs — no longer has a type selector)
    const strategy: Record<string, unknown> = { type: 'reactive' }; // backward compat
    if (draft.cooldownSeconds !== '120') strategy.cooldownSeconds = parseIntVal(draft.cooldownSeconds);
    if (draft.evaluationPeriods !== '1') strategy.evaluationPeriods = parseIntVal(draft.evaluationPeriods);
    if (draft.enabledSources.has('reactive')) {
      if (draft.scaleUpThreshold !== '1') strategy.scaleUpThreshold = parseIntVal(draft.scaleUpThreshold);
      if (draft.scaleDownDelay !== '900') strategy.scaleDownDelaySeconds = parseIntVal(draft.scaleDownDelay);
    }
    if (draft.enabledSources.has('scheduled')) {
      if (draft.preScaleMinutes !== '15') strategy.preScaleMinutes = parseIntVal(draft.preScaleMinutes);
      if (!draft.enabledSources.has('reactive') && draft.scaleDownDelay !== '900') {
        strategy.scaleDownDelaySeconds = parseIntVal(draft.scaleDownDelay);
      }
    }
    if (draft.enabledSources.has('predictive')) {
      // Forecast horizon — should match backend warm-up time. Default 4 min.
      if (draft.lookaheadMinutes !== '30') strategy.lookaheadMinutes = parseIntVal(draft.lookaheadMinutes);
    }

    // Predictive: shadow mode runs the pipeline but contributes nothing to
    // mediation. Predictive section enabled with shadow flag → demandSources
    // emits predictive=false + predictiveShadow=true. Section enabled
    // without shadow → predictive=true.
    const predictiveActive = draft.enabledSources.has('predictive') && !draft.predictiveShadowMode;
    const predictiveShadowOn = draft.enabledSources.has('predictive') && draft.predictiveShadowMode;
    const demandSources: Record<string, unknown> = {
      reactive: draft.enabledSources.has('reactive'),
      scheduled: draft.enabledSources.has('scheduled'),
      predictive: predictiveActive,
      backfill: draft.enabledSources.has('backfill'),
    };
    if (predictiveShadowOn) demandSources.predictiveShadow = true;

    const backfill = draft.enabledSources.has('backfill')
      ? {
          threshold: parseNum(draft.backfillThreshold) ?? 0.5,
          ...(draft.backfillMultiplier !== '1.5' ? { growthMultiplier: parseNum(draft.backfillMultiplier) } : {}),
          ...(draft.backfillAdaptive ? { adaptive: true } : {}),
          ...(draft.backfillAdaptive && draft.backfillAdaptiveWindow !== '5'
            ? { adaptiveWindowMinutes: parseIntVal(draft.backfillAdaptiveWindow) }
            : {}),
          ...(draft.backfillAdaptive && draft.backfillAdaptiveSaturation !== '1'
            ? { adaptiveSaturationArrivalsPerMin: parseNum(draft.backfillAdaptiveSaturation) }
            : {}),
        }
      : undefined;

    // Preserve any existing observation lat/lng or explicit radiusKm override
    // the operator set via raw settings — the UI only exposes the friendly
    // tier, but we don't want to wipe deliberate power-user overrides on save.
    const observation: Record<string, unknown> = {};
    if (settings?.observation?.lat !== undefined) observation.lat = settings.observation.lat;
    if (settings?.observation?.lng !== undefined) observation.lng = settings.observation.lng;
    if (existingRadiusKmOverride !== null) observation.radiusKm = existingRadiusKmOverride;
    // Always persist the tier when the operator has explicitly chosen one
    // (i.e. anything non-default) or when there's no existing override.
    // Omitting the tier silently falls back to the server default ("global"),
    // which is what we want for the new-pool-left-unchanged case.
    if (draft.observationRadiusTier !== 'global' || settings?.observation?.radiusTier) {
      observation.radiusTier = draft.observationRadiusTier;
    }

    // Soft floor — only emitted when enabled; absent means disabled (the
    // server default). Non-default tuning knobs are emitted alongside.
    const softFloor = draft.softFloorEnabled
      ? {
          enabled: true,
          ...(draft.softFloorAfterIdleSeconds !== '3600'
            ? { afterIdleSeconds: parseIntVal(draft.softFloorAfterIdleSeconds) }
            : {}),
          ...(draft.softFloorMinSlots !== '0'
            ? { minSlots: parseIntVal(draft.softFloorMinSlots) }
            : {}),
        }
      : undefined;

    // Drain reaper — default-ON server-side, so emission is inverted from
    // softFloor: only non-default values are written; an empty object means
    // "all defaults" and is omitted entirely.
    const reaper: Record<string, unknown> = {};
    if (!draft.reaperEnabled) reaper.enabled = false;
    if (draft.reaperIdleGraceSeconds !== '720') reaper.idleGraceSeconds = parseIntVal(draft.reaperIdleGraceSeconds);
    if (draft.reaperBootImmunitySeconds !== '720') reaper.bootImmunitySeconds = parseIntVal(draft.reaperBootImmunitySeconds);
    if (draft.reaperMaxReapsPerTick !== '5') reaper.maxReapsPerTick = parseIntVal(draft.reaperMaxReapsPerTick);

    // Predictive top-level fields (IL-136). Only emitted when explicitly
    // set; absent means server-side default (P75 / UTC).
    const predictivePatch: Record<string, unknown> = {};
    if (draft.enabledSources.has('predictive')) {
      const q = parseNum(draft.predictiveQuantile);
      if (q !== undefined && q !== 0.9) predictivePatch.predictiveQuantile = q;
      const tz = draft.predictiveTimezone.trim();
      if (tz) predictivePatch.timezone = tz;
    }

    const settingsJson = JSON.stringify({
      backends,
      allocationStrategy: draft.infrastructure.allocationStrategy,
      scaling: {
        min: parseIntVal(draft.scalingMin) ?? 0,
        max: parseIntVal(draft.scalingMax) ?? 100,
        buffer: parseIntVal(draft.scalingBuffer) ?? 0,
      },
      strategy,
      demandSources,
      ...(backfill ? { backfill } : {}),
      ...(softFloor ? { softFloor } : {}),
      ...(Object.keys(reaper).length > 0 ? { reaper } : {}),
      ...(Object.keys(observation).length > 0 ? { observation } : {}),
      ...predictivePatch,
      tenancy: draft.tenancy,
      ...(draft.orchestratorVersion ? { orchestratorVersion: draft.orchestratorVersion } : {}),
      // Passthrough for settings keys the UI doesn't edit — the serializer
      // rebuilds the JSON from scratch, so anything not carried over here
      // would be wiped on save.
      ...(settings?.cells !== undefined ? { cells: settings.cells } : {}),
      ...(settings?.maxConcurrentLimit !== undefined ? { maxConcurrentLimit: settings.maxConcurrentLimit } : {}),
      ...(settings?.warmCoordinatorV2 !== undefined ? { warmCoordinatorV2: settings.warmCoordinatorV2 } : {}),
    });

    return {
      settingsJson,
      capacityScheduleJson: draft.capacitySchedule ? JSON.stringify(draft.capacitySchedule) : null,
      name: draft.name.trim(),
      region: draft.region,
      active: draft.active,
    };
  }, [draft, pool, existingRadiusKmOverride]);

  return {
    draft,
    patch,
    addSource,
    removeSource,
    restoreDemandSourceDefaults,
    dirty,
    discard,
    markSaved,
    serialize,
    existingRadiusKmOverride,
  };
}
